import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';

import Button from '@shared/atoms/Button';
import FormInput from '@shared/atoms/FormFields/FormInput';
import T from '@shared/atoms/Typography';
import DragAndDrop from '@shared/molecules/DragAndDrop';
import ItemBox from '@shared/molecules/ItemBox';

import theme from '@shared/themes/default';

import { courseStatus } from '@shared/const';

import EditField from './EditField';
import LearningPointDrawer from './LearningPointDrawer';

import { useCourse } from '../../../../../contexts/CourseContext';

import { LEVEL_FIELD_TYPE } from '../const';

const ArticleStyled = styled.article`
  background: ${theme.palette.darkDistinct};
  padding: ${theme.spacing.s};
`;

const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.m1};
`;

const LearningPointsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${theme.spacing.m2};
  row-gap: ${theme.spacing.s};
`;

const LearningPointsHeader = styled.div`
  align-items: center;
  column-gap: ${theme.spacing.m1};
  display: flex;
`;

const PointsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.xs};
`;

const DescriptionDetails = ({
  changeValues,
  formErrors,
  formInputFields,
  formProperties,
  formValues,
  handleBlur,
  handleChange,
  selectedLanguage,
}) => {
  const { t } = useTranslation();

  const { course, locked, submitting } = useCourse();

  const [learningPoints, setLearningPoints] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLearningPoint, setSelectedLearningPoint] = useState(null);

  const isPublished = course.status === courseStatus.PUBLISHED;
  const isLocked = submitting || (isPublished && locked);

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedLearningPoint(null);
  };

  const handleSaveDrawer = (newValue) => {
    const newValues = learningPoints;
    newValues[newValue.id] = newValue;
    const formattedValues = formValues.learningPoints.reduce((acc, curr) => {
      if (curr.languageCode === selectedLanguage) {
        acc.push({
          languageCode: selectedLanguage,
          values: newValues.map((point) => point.label),
        });
      } else {
        acc.push(curr);
      }
      return acc;
    }, []);
    if (
      !formattedValues.find(
        (translation) => translation.languageCode === selectedLanguage
      )
    ) {
      formattedValues.push({
        languageCode: selectedLanguage,
        values: newValues.map((point) => point.label),
      });
    }
    handleChange({
      name: 'learningPoints',
      value: formattedValues,
    });
    handleCloseDrawer(true);
  };

  const handleAddKeyPoint = () => {
    setSelectedLearningPoint({ id: `${learningPoints.length}`, label: '' });
    setDrawerOpen(true);
  };

  const handleKeyDelete = (id) => {
    handleChange({
      name: 'learningPoints',
      value: formValues.learningPoints.reduce((acc, curr) => {
        if (curr.languageCode === selectedLanguage) {
          acc.push({
            languageCode: selectedLanguage,
            values: learningPoints.toSpliced(id, 1).map((point) => point.label),
          });
        } else {
          acc.push(curr);
        }
        return acc;
      }, []),
    });
  };

  const handleKeyDrag = (newPointsRaw) => {
    setLearningPoints(newPointsRaw);
    handleChange({
      name: 'learningPoints',
      value: formValues.learningPoints.reduce((acc, curr) => {
        if (curr.languageCode === selectedLanguage) {
          acc.push({
            languageCode: selectedLanguage,
            values: newPointsRaw.map((point) => point.label),
          });
        } else {
          acc.push(curr);
        }
        return acc;
      }, []),
    });
  };

  const handleKeyEdit = (id) => {
    setSelectedLearningPoint(learningPoints[id]);
    setDrawerOpen(true);
  };

  const keyButtons = [
    {
      label: t('backoffice.courses.descriptionTab.edit'),
      handler: handleKeyEdit,
    },
    {
      label: t('generic.delete'),
      handler: handleKeyDelete,
      textStyle: true,
    },
  ];
  const renderItem = (item) => {
    return (
      <ItemBox
        animated
        buttons={!isLocked ? keyButtons : null}
        draggable={!isLocked}
        id={item.id}
        menuItems={[]}
        theme="dark"
        title={item.label}
      />
    );
  };

  useEffect(() => {
    setLearningPoints(
      formValues.learningPoints
        ?.find((translation) => translation.languageCode === selectedLanguage)
        ?.values?.map((value, idx) => ({ id: `${idx}`, label: value })) || []
    );
  }, [formValues.learningPoints, selectedLanguage]);

  return (
    <ArticleStyled>
      <FormFieldsWrapper>
        {formInputFields.map((field) => (
          <FormInput
            id={field.id}
            name={field.id}
            key={field.id}
            disabled={isLocked}
            errors={formErrors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            infoText={field.infoText}
            label={field.label}
            locale={selectedLanguage}
            minRows={field.minRows}
            multiline={!!field.minRows}
            placeholder={field.placeholder}
            properties={formProperties}
            showMaxLength
            theme="dark"
            values={formValues}
          />
        ))}
        <EditField
          changeValues={changeValues}
          handleChange={handleChange}
          selectedLanguage={selectedLanguage}
          type={LEVEL_FIELD_TYPE.PREREQUISITES}
          values={formValues}
        />
        <EditField
          changeValues={changeValues}
          handleChange={handleChange}
          selectedLanguage={selectedLanguage}
          type={LEVEL_FIELD_TYPE.TYPICAL_LEARNER}
          values={formValues}
        />
      </FormFieldsWrapper>
      <LearningPointsWrapper>
        <LearningPointsHeader>
          <T color={theme.palette.lightPure} variant="headingS">
            {t('backoffice.courses.descriptionTab.learningPointsCount', {
              max: 6,
              total: learningPoints.length,
            })}
          </T>
          <Button
            disabled={learningPoints.length >= 6 || isLocked}
            iconRight={AddIcon}
            onClick={handleAddKeyPoint}
            sx={{
              textTransform: 'uppercase',
            }}
            type="secondaryOutline"
          >
            {t('backoffice.courses.add')}
          </Button>
        </LearningPointsHeader>
        <PointsWrapper>
          <DragAndDrop
            isLocked={isLocked}
            items={learningPoints}
            onDragEnd={handleKeyDrag}
            renderItem={renderItem}
          />
        </PointsWrapper>
      </LearningPointsWrapper>
      <LearningPointDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSaveDrawer}
        learningPoint={selectedLearningPoint}
      />
    </ArticleStyled>
  );
};

DescriptionDetails.propTypes = {
  changeValues: PropTypes.func.isRequired,
  formErrors: PropTypes.object.isRequired,
  formInputFields: PropTypes.array.isRequired,
  formProperties: PropTypes.object.isRequired,
  formValues: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  selectedLanguage: PropTypes.string.isRequired,
};

export default DescriptionDetails;
